import { useEffect, useState, useMemo } from "react";
import { useAppSelector } from "./redux";
import { Themes } from "util/CommonConstant";

/**
 * Custom hook for detecting dark mode with smooth re-renders
 * Handles both manual theme selection and system preference changes
 * @returns {boolean} isDarkMode - Whether dark mode is currently active
 */
const useDarkMode = (): boolean => {
  const mode = useAppSelector((state) => state.common.mode);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    // Initialize with current system preference
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  // Memoize the dark mode calculation to prevent unnecessary re-renders
  const isDarkMode = useMemo(() => {
    return mode === Themes.DARK || (mode !== Themes.LIGHT && systemPrefersDark);
  }, [mode, systemPrefersDark]);

  // Listen for system theme preference changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    // Add event listener for system theme changes
    mediaQuery.addEventListener("change", handleSystemThemeChange);

    // Cleanup event listener
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, []);

  return isDarkMode;
};

export default useDarkMode;
